import { Type } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { PieSeries } from '@amcharts/amcharts4/charts';

import { AbstractChartOptionsModel } from '../abstract-chart-options.model';
import { ChartMetadataModel } from '../chart-metadata.model';
import { ChartModel } from '../chart.model';
import { ChartSeries } from '../chart-series.model';
import { ChartSizeHelper } from '../../helpers/chart-size.helper';
import { PieChartOptionsModel } from './pie-chart-options.model';
import { SeriesModel } from '../series.model';
import { CustomLegendItem } from '../custom-legend-item.model';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { LabelDisplayOptionsEnum } from '../../../../Common/Misc/enums/label-display-options.enum';
import { ChartMetricsEnum } from '../../../../Common/Reports/enums/chart-metrics.enum';
import { DataPointModel } from '../data-point.model';
import { image, imageExportFormatsList } from '../../../../Common/Misc/helpers/image/image.helper';
import { manualChartExport } from '../../../../Common/Charts/helpers/manualChartExport.helper';
import { download } from '../../../../Common/Misc/helpers/download/download.helper';


export class PieChart extends ChartModel {

    declare amchart: am4charts.PieChart;
    declare options: PieChartOptionsModel;

    readonly chartType: Type<am4charts.PieChart> = am4charts.PieChart;
    readonly maxItemsPerLegendColumn: number = 8;
    readonly exportTypes: (keyof am4core.IExportOptions)[] = ['json', 'csv'];
    readonly TWO_COLUMN_WIDTH = 315;

    constructor(
        series: SeriesModel[],
        options: AbstractChartOptionsModel = {},
        metadata?: ChartMetadataModel,
        chartSeries?: ChartSeries[]
    ) {
        super(series, options, metadata);
        this.chartSeries = chartSeries;
    }

    public initChart(element: HTMLElement | am4core.Container, chartType: Type<am4charts.SerialChart>): void {
        super.initChart(element, this.chartType);

        this.amchart.radius = am4core.percent(this.options.radius);
        this.amchart.innerRadius = am4core.percent(this.options.innerRadius);

        this.setUpSeries();
        this.setUpLegend();
        this.setUpResponsiveness();
    }

    setUpSeries(): void {
        this.chartSeries.forEach((chartSeries, index) => this.addSeries(index, chartSeries));
    }

    addSeries(seriesIndex: number, chartSeries: ChartSeries): void {
        const pieSeries = this.amchart.series.push(new PieSeries());

        pieSeries.dataFields.category = 'label';
        pieSeries.dataFields.value = 'value' + seriesIndex;
        if (chartSeries.radius) {
            pieSeries.radius = am4core.percent(chartSeries.radius);
        }
        if (chartSeries.innerRadius) {
            pieSeries.innerRadius = am4core.percent(chartSeries.innerRadius);
        }
        pieSeries.ticks.template.disabled = true;
        pieSeries.labels.template.disabled = true;
        pieSeries.slices.template.states.getKey('active').properties.shiftRadius = 0;
        pieSeries.readerTitle = chartSeries.label;
        pieSeries.hiddenInLegend = chartSeries.hiddenInLegend;

        const sliceTemplate = pieSeries.slices.template;
        sliceTemplate.stroke = am4core.color(chartColors.agprWhite);
        sliceTemplate.strokeWidth = 1;
        sliceTemplate.strokeOpacity = 1;
        sliceTemplate.cornerRadius = 3;
        // Create a base filter effect (as if it's not there) for the hover to return to
        const shadow = pieSeries.slices.template.filters.push(new am4core.DropShadowFilter);
        shadow.opacity = 0;
        sliceTemplate.adapter.add('fill', (fill, target) => {
            if (target.dataItem.dataContext?.['label'] === 'hasNoData') {
                if (target.dataItem.dataContext?.['value' + seriesIndex] > 0) {
                    const pattern = new am4core.LinePattern();
                    pattern.width = 8;
                    pattern.height = 8;
                    pattern.strokeWidth = 1;
                    pattern.stroke = am4core.color(chartColors.agprLighterGrey);
                    pattern.backgroundFill = am4core.color(chartColors.agprGreyMediumE5);
                    pattern.backgroundOpacity = 1;
                    pattern.rotation = 45;
                    return pattern;
                } else {
                    return target.dataItem.dataContext['color'];
                }
            } else {
                return target.dataItem.dataContext['color'];
            }
        });
        sliceTemplate.adapter.add('tooltipText', (text, target) => {
            if (this.options.hideTooltip) {
                return '';
            }
            if (
                target.tooltipDataItem?.dataContext?.['label'] === 'hasNoData'
                && target.tooltipDataItem?.dataContext?.['value' + seriesIndex] > 0
            ) {
                return this.metadata?.noDataFromDateRangeLabel;
            }

            return text;
        });

        // set tooltip style
        pieSeries.tooltip.getFillFromObject = false;
        pieSeries.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        pieSeries.tooltip.background.stroke = am4core.color('#ced4da');
        pieSeries.tooltip.background.cornerRadius = 8;
        pieSeries.tooltip.background.fillOpacity = 1;
        pieSeries.tooltip.background.filters.clear();
        pieSeries.tooltip.keepTargetHover = true;
        pieSeries.tooltip.label.padding(5, 10, 5, 10);
        pieSeries.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        pieSeries.tooltip.label.interactionsEnabled = true;

        pieSeries.numberFormatter.numberFormat = '#.#a';
        pieSeries.numberFormatter.smallNumberThreshold = 0;
        pieSeries.numberFormatter.bigNumberPrefixes = [
            { number: 1e+3, suffix: 'K' },
            { number: 1e+6, suffix: 'M' },
            { number: 1e+9, suffix: 'B' },
            { number: 1e+12, suffix: 'T' },
            { number: 1e+15, suffix: 'Q' }
        ];

        pieSeries.legendSettings.labelText = '{category}';

        this.setUpSeriesScreenReaderText(pieSeries, chartSeries);
    }

    setUpSeriesScreenReaderText(pieSeries: PieSeries, chartSeries: ChartSeries): void {
        // Text to use as aria-label for each column
        let srText = '{category}, ';
        if (this.metadata?.labelDisplayOptions) {

            const doCount: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
            const doPercent: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent);

            let prefix = '';
            if (chartSeries.metric === ChartMetricsEnum.ave) {
                prefix = '$';
            }

            if (doCount) {
                pieSeries.legendSettings.labelText += ' -';
                if (doPercent) {
                    pieSeries.legendSettings.valueText = `[bold]${prefix}{value}[/], [bold]{value.percent.formatNumber(\'#.#\')}%[/]`;
                    srText += `${prefix}{value}, {value.percent.formatNumber(\'#.#\')}%`;
                } else {
                    pieSeries.legendSettings.valueText = `[bold]${prefix}{value}[/]`;
                    srText += `${prefix}{value}`;
                }
            } else if (doPercent) {
                pieSeries.legendSettings.labelText += ' -';
                pieSeries.legendSettings.valueText = '[bold]{value.percent.formatNumber(\'#.#\')}%[/]';
                srText += '{value.percent.formatNumber(\'#.#\')}%';
            } else {
                // LegendSettings changes, null or "" don't clear it, only whitespace does
                pieSeries.legendSettings.valueText = ' ';
                srText = '{category}, ';
            }
        } else {
            // this is necessary to reset the value in some situations
            pieSeries.legendSettings.labelText = '{category}';
            pieSeries.legendSettings.valueText = ' ';
            srText = '{category}, ';
        }

        pieSeries.itemReaderText = srText;
    }

    setUpLegend(): void {
        if (this.options.legendContainerElement) {
            this.initCustomLegend();
        } else {
            this.amchart.legend = new am4charts.Legend();
            this.amchart.legend.position = this.options.legendPosition || 'right';
            this.amchart.legend.scrollable = true;
            this.amchart.legend.markers.template.scale = 0.75;
            this.amchart.legend.itemContainers.template.paddingTop = 2;
            this.amchart.legend.itemContainers.template.paddingBottom = 2;
            this.amchart.legend.itemContainers.template.focusable = false;
            this.amchart.legend.fontSize = '0.75rem';

            this.amchart.legend.labels.template.truncate = true;
            this.amchart.legend.valueLabels.template.align = 'left';
            this.amchart.legend.valueLabels.template.textAlign = 'start';

            // this line for some reason fixes a style issue in the pdf export
            // but doesn't work when added to setExportStyles
            this.amchart.legend.strokeOpacity = 0;

            this.amchart.legend.width = 170;
            const smallChartWidth = ChartSizeHelper.getWidthAtMinCanvas(6, 4);
            if (this.amchart.pixelWidth > smallChartWidth) {
                this.amchart.legend.width = 300;
            }

            this.amchart.legend.dragWhileResize = true;
        }
    }

    getCustomLegendItems(): CustomLegendItem[] {
        const items: CustomLegendItem[] = [];
        if (this.isContentEmpty()) {
            this.legendWrapper.style.display = 'none';
            return items;
        } else {
            this.legendWrapper.style.display = 'flex';
        }
        let doCount = false;
        let doPercent = false;
        if (this.metadata?.labelDisplayOptions) {
            doCount = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
            doPercent = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent);
        }
        this.amchart.data?.forEach((point, i) => {
            if (point.label === 'hasNoData') {
                return;
            }
            const lastSeriesIndex = this.amchart.series.length - 1;
            const series = this.amchart.series.values[lastSeriesIndex];
            if (!series) {
                return;
            }
            const slice = series.slices.getIndex(i);
            if (!point || !slice) {
                return;
            }
            const item: CustomLegendItem = {
                label: point.label,
                color: slice.fill.toString(),
                mouseover: () => {
                    if (!slice) {
                        return;
                    }
                    slice.isHover = true;
                },
                mouseout: () => {
                    if (!slice) {
                        return;
                    }
                    slice.isHover = false;
                },
                // TODO: hide is just fading it out and not updating the rest of the chart to fill the empty space
                // click: () => {
                //     if (!slice) {
                //         return;
                //     }
                //     if (slice.visible) {
                //         slice.hide(200);
                //     } else {
                //         slice.show(200);
                //     }
                // }
            };
            const count = point['value' + lastSeriesIndex];
            if (doCount && count !== null && count !== undefined) {
                item.count = count;
            }
            const percent = point['percent' + lastSeriesIndex];
            if (doPercent && percent !== null && percent !== undefined) {
                item.percent = percent;
            }
            items.push(item);
        });

        return items;
    }

    setUpResponsiveness(): void {
        if (this.options.responsive) {
            this.setUpSharedReportResponsiveness();
        } else {
            this.setUpMainAppResponsiveness();
        }
    }

    setUpSharedReportResponsiveness(): void {
        this.amchart.radius = am4core.percent(90);
        this.amchart.innerRadius = am4core.percent(50);
    }

    setUpMainAppResponsiveness(): void {
        const smallChartHeight = ChartSizeHelper.getCellHeight();
        this.amchart.events.on('sizechanged', ev => {
            if (this.options.legendPosition) {
                return;
            }

            const chart = ev.target;

            // set legend position
            if (chart.pixelWidth > this.TWO_COLUMN_WIDTH) {
                if (this.primarySeriesLabel) {
                    this.primarySeriesLabel.dx = 0;
                }

                this.amchart.chartContainer.width = am4core.percent(50);
                this.amchart.legend.position = 'right';
                this.amchart.legend.width = this.amchart.contentWidth * 0.4;
                this.amchart.legend.maxHeight = Math.abs(chart.pixelHeight - 85);
            } else {
                if (this.primarySeriesLabel) {
                    this.primarySeriesLabel.dx = -10;
                }
                this.amchart.chartContainer.width = am4core.percent(100);
                this.amchart.legend.position = 'bottom';
                this.amchart.legend.width = am4core.percent(100);

                const maxLegendHeight = Math.max(chart.pixelHeight - chart.pixelWidth, 70);
                this.amchart.legend.maxHeight = maxLegendHeight;
            }

            // Seperating the legend markers resizing from the title and padding to leave reports unaffected
            if (chart.pixelWidth > this.TWO_COLUMN_WIDTH && chart.pixelHeight > smallChartHeight) {
                this.amchart.legend.markers.template.scale = 1;
                this.amchart.legend.fontSize = '0.875rem';
            } else {
                this.amchart.legend.markers.template.scale = 0.75;
                this.amchart.legend.fontSize = '0.75rem';
            }

            this.amchart.legend.afterDraw();
        });
    }

    updateData(data: DataPointModel[][]): void {
        this.rawData = JSON.parse(JSON.stringify(data));

        if (!this.amchart) {
            return;
        }
        if (!data) {
            this.amchart.data = null;
            this.ready.next(true);
            return;
        }

        if (!data?.includes(null) && this.isContentEmpty()) {
            this.amchart.series.values.forEach(series => series.disabled = true);
            if (this.amchart.legend) {
                this.amchart.legend.disabled = true;
            }
            this.showNoContentMessage();
        } else {
            const d = this.handleNoDataSeries(JSON.parse(JSON.stringify(data[0])));
            this.amchart.data = d;
        }

        this.updateSeriesStyles();
        this.updateCustomLegend();
        this.ready.next(true);
    }

    handleNoDataSeries(data: DataPointModel[]): DataPointModel[] {
        const seriesTotalResults = this.chartSeries.map(x => null);
        this.chartSeries.forEach((series, index) => {
            seriesTotalResults[index] = data.reduce((x, y) => x + (((y['value' + index] as number) || 0)), 0);
        });
        if (seriesTotalResults.some(x => x === 0)) {
            const dataPoint = {
                label: 'hasNoData'
            };
            seriesTotalResults.forEach((s, i) => {
                dataPoint['value' + i] = s === 0 ? 1 : 0;
            });
            data.push(dataPoint);
        }

        // we never want to show to "added" no data point in the legend
        this.amchart?.legend?.adapter.add('data', (dataPoints) => {
            return dataPoints.filter(item => item.dataContext['label'] !== 'hasNoData');
        });

        return data;
    }

    public updateSeriesStyles(): void {
        if (this.styles.colorMap && Object.keys(this.styles.colorMap).length > 0) {
            this.amchart.data.forEach(dataPoint => {
                if (dataPoint.key && this.styles.colorMap[dataPoint.key]) {
                    dataPoint.color = this.styles.colorMap[dataPoint.key];
                } else {
                    // share of voice
                    const seriesIndex = this.series.findIndex(x => x.label === dataPoint?.mappingKeys?.['source']);
                    dataPoint.color = this.options.seriesColorMapKey
                        ? this.styles.colorMap[this.options.seriesColorMapKey + (seriesIndex + 1)]
                        : this.styles?.colorMap[`seriesColor${seriesIndex}`];
                }
            });
        }
    }

    public async export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields = {
            label: this.options.label,
        };
        // export outer ring first
        for (let index = this.chartSeries.length - 1; index >= 0; index--) {
            const chartSeries = this.chartSeries[index];
            dataFields['value' + index] = chartSeries.exportLabel;
            dataFields['percent' + index] = chartSeries.exportPercentLabel;
        }

        const exportType = this.exportTypes.find(exportOption => exportOption === type);
        // necessary to export full data, without max number of attributes
        if (exportType) {
            manualChartExport.exportFlatSeriesData(exportType, this.rawData[0], dataFields, filename);

            return;
        }

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;
        this.amchart.exporting.data = this.rawData[0];

        // HACK: fixes an issue in safari when exporting charts, SEE AD-11586
        if (imageExportFormatsList.includes(type)) {
            let imageData: string = await this.amchart.exporting.getSVG('svg');

            const url = new URL(window.location.href);
            url.hash = '';
            const cleanUrl = url.toString();
            const escapedUrl = encodeURIComponent(cleanUrl);

            if (imageData.includes(escapedUrl)) {
                imageData = imageData.replace(new RegExp(escapedUrl, 'gi'), '');

                const converted = await image.convertSvgDataToBlob(imageData, type);

                download.blob(converted, `${filename}.${type}`);

                return true;
            }
        }
        // END HACK

        return this.amchart.exporting.export(type);
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);


        // these lines + the paired ones below fixes donuts being sized inconsistently during export
        const smallChartWidth = ChartSizeHelper.getWidthAtMinCanvas(6, 4);
        if (this.amchart.pixelWidth > smallChartWidth) {
            this.amchart.chartAndLegendContainer.dx = 125;
            this.amchart.legend.scale = 1.5;
            this.amchart.legend.dx = -50;
            this.amchart.series.values.forEach(series => {
                series.slices.template.strokeWidth = 2;
                series.scale = 1.5;
                series.dy = 0;
                series.dx = -100;
            });
        }
    }
    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        if (!this.amchart) {
            return;
        }
        this.amchart.chartAndLegendContainer.dx = 0;
        this.amchart.legend.scale = 1;
        this.amchart.legend.dx = 0;
        this.amchart.series.values.forEach(series => {
            series.slices.template.strokeWidth = 1;
            series.scale = 1;
            series.dy = 0;
            series.dx = 0;
        });

        super.unsetExportStyles();
    }

    public isContentEmpty(): boolean {
        const totalResults = this.rawData?.[0]?.reduce((x, y) => {
            return x + (y['value0'] as number || 0) + (y['value1'] as number || 0);
        }, 0);

        return !totalResults;
    }
}
